import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page, Button } from 'equasion-lib';
import {
  FiXCircle,
  FiCheckCircle,
  FiAlertTriangle,
  FiPrinter,
  FiNavigation,
  FiX
} from 'react-icons/fi';
import WarehouseProductDisplay from '@/lib/shared-coms/WarehouseProductDisplay';
import ZoneScanButton from '@/lib/shared-coms/ZoneScanner';
import { companyUsesTotes } from '@/lib/totes';
import { salesOrdersTabPath } from '@/lib/pickVerifyShip';
import { documentActionLabel, type PrintableOrderDocument } from '@/lib/rxPrint';
import './SalesOrderPicking.scss';

const isSerializedLine = ({ product }) => {
  const { fdaProduct, upcProduct } = product || {};
  const { deaSchedule } = fdaProduct || {};
  return deaSchedule !== 'OTC' && deaSchedule !== 'Rx Exempt' && !upcProduct;
};

const pickedCount = (item) =>
  isSerializedLine(item)
    ? item.serializedItemOfStatusCount || 0
    : item.unserializedItemOfStatusCount || 0;

/** "Stop 3 · A-12" for a sequenced zone, the bare zone name otherwise, or a nudge when nothing is pickable. */
const stopLabel = (stop) => {
  if (!stop) return null;
  if (!stop.zone) return <span className="pick-stop pick-stop--none">No stock found</span>;
  const others = stop.zones.length - 1;
  return (
    <span className="pick-stop">
      {typeof stop.zone.pickSequence === 'number' && (
        <span className="pick-stop__rank">Stop {stop.rank + 1}</span>
      )}
      <span className="pick-stop__zone">{stop.zone.name}</span>
      {others > 0 && <span className="pick-stop__others">+{others} more</span>}
    </span>
  );
};

const downloadFile = async (url: string, filename: string) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Download failed (${response.status})`);
    const buf = await response.arrayBuffer();
    const blob = new Blob([buf], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch {
    window.open(url, '_blank');
  }
};

function SalesOrderPicking({
  salesOrder,
  company,
  companyId,
  orderUpdate,
  splitOrderAtPicking,
  pickPath,
  setStartZoneId,
  // RxPrint on: the picking slip goes to its mapped printer; off, the button says Download.
  rxPrintEnabled = false,
  sendingKey = null as string | null,
  onSendToPrinter = undefined as
    | undefined
    | ((key: string, documents: PrintableOrderDocument[]) => void)
}) {
  const [completeOrder, setCompleteOrder] = useState(false);
  const [partiallyPickedOpen, setPartiallyPickedOpen] = useState(false);
  const navigate = useNavigate();
  const {
    id: salesOrderId,
    vendorSalesOrderNumber,
    orderItems,
    tote,
    client,
    pickingSlipDocument
  } = salesOrder || {};
  const { companyName } = client || {};
  // Evaluated on EVERY render from the freshly-queried company (SalesOrderPicking.data.tsx) —
  // never stored in state, never written to the order, never cached across orders. That is what
  // makes the toggle instantly reversible in both directions: turning totes back on re-blocks an
  // order picked without one, and turning them off immediately unblocks it, with no per-order
  // bookkeeping and no migration of anything in flight.
  const usesTotes = companyUsesTotes(company);
  const orderItemsSafe = orderItems ?? [];
  const totalQuantity = orderItemsSafe.reduce((a, { quantity }) => a + quantity, 0);
  // The walk. `stopByItemId` is the zone each line is picked from (always, even with no path
  // set up — then it is simply the first zone by name). `pathConfigured` decides whether the
  // LIST is reordered: with a path, one list in walk order, finished lines sinking to the
  // bottom; without one, the two familiar blocks in order-line order.
  const stopByItemId = new Map<string, any>(
    (pickPath?.stops || []).map((stop) => [stop.orderItemId, stop])
  );
  const pathConfigured = !!pickPath?.pathConfigured;
  const walkOrdered = pathConfigured
    ? [...orderItemsSafe].sort((a, b) => {
        const aDone = a.quantity === pickedCount(a);
        const bDone = b.quantity === pickedCount(b);
        if (aDone !== bDone) return aDone ? 1 : -1;
        return (
          (stopByItemId.get(a.id)?.rank ?? Number.MAX_SAFE_INTEGER) -
          (stopByItemId.get(b.id)?.rank ?? Number.MAX_SAFE_INTEGER)
        );
      })
    : null;
  const anchorZone = pickPath?.anchorZone;
  const anchorSource = pickPath?.anchorSource;
  const scheduledSalesOrderItems = orderItemsSafe.filter(isSerializedLine);
  const allComplete = orderItemsSafe.every(
    ({ quantity, serializedItemOfStatusCount = 0, unserializedItemOfStatusCount = 0 }) =>
      quantity === serializedItemOfStatusCount + unserializedItemOfStatusCount
  );
  const someComplete = orderItemsSafe.some(
    ({ serializedItemOfStatusCount, unserializedItemOfStatusCount }) =>
      serializedItemOfStatusCount > 0 || unserializedItemOfStatusCount > 0
  );
  const unserializedSalesOrderItems = orderItemsSafe.filter((item) => !isSerializedLine(item));

  const openLine = (item) => {
    const { id, product, isShortDated } = item;
    const zoneId = stopByItemId.get(id)?.zone?.id;
    const zoneParam = zoneId ? `&zoneId=${zoneId}` : '';
    if (isSerializedLine(item)) {
      navigate({
        pathname: '/sales-order-pick-scan',
        search: `?salesOrderId=${salesOrderId}&salesOrderItemId=${id}${zoneParam}`
      });
      return;
    }
    const { id: upcProductId } = product?.upcProduct || {};
    const { id: fdaProductId } = product?.fdaProduct || {};
    navigate(
      `/sales-order-unserialized-pick-scan/${salesOrderId}?fdaProductId=${fdaProductId}&upcProductId=${upcProductId}&isShortDated=${!!isShortDated}${zoneParam}`
    );
  };

  const renderLine = (item) => {
    const { id, product, quantity, isShortDated, isChargeback } = item;
    const picked = pickedCount(item);
    return (
      <div
        key={id}
        className={['order-item', quantity === picked && 'complete'].filter(Boolean).join(' ')}
        onClick={() => openLine(item)}
      >
        <div className="product-info">
          <WarehouseProductDisplay
            product={product}
            // A finished line has nothing left to locate, and its zones may already be empty.
            prefix={quantity === picked ? null : stopLabel(stopByItemId.get(id))}
          >
            {isShortDated && (
              <span style={{ color: 'var(--orange, orange)', fontWeight: 'bold' }}>
                SHORT DATED
              </span>
            )}
            {/* Two different rules, so two different colours rather than one
                "special line" badge: orange means the LOT must be short-dated,
                purple means the UNITS must have come from one named supplier.
                A line can carry both, and they stack, because
                WarehouseProductDisplay lays its children out in a column. Purple
                is used nowhere else on the floor screens, so it cannot be read as
                a shade of the short-dated warning. */}
            {isChargeback && (
              <span style={{ color: 'var(--purple, purple)', fontWeight: 'bold' }}>
                CHARGEBACK
              </span>
            )}
          </WarehouseProductDisplay>
        </div>
        <div className="picking-completion">
          {picked}/{quantity}
        </div>
      </div>
    );
  };
  // Almost no order has a chargeback line, so the explainer is conditional: a rule shown on every
  // order is a rule nobody reads by the second week, and it would then be noise on the one order
  // where it decides whether the pick is refused.
  const hasChargebackItems = orderItemsSafe.some(({ isChargeback }) => !!isChargeback);

  const submitSalesOrderUpdate = async () => {
    try {
      await orderUpdate({
        variables: {
          orderId: salesOrderId,
          orderAttributes: {
            vendorStatus: 'shipping'
          },
          companyId: companyId
        }
      });
      setCompleteOrder(salesOrder);
    } catch (e) {
      console.log(`ERROR UPDATING ORDER: ${e}`);
    }
  };

  const submitSplitOrderAtPicking = async () => {
    setPartiallyPickedOpen(false);
    try {
      const { data } = await splitOrderAtPicking({
        variables: {
          salesOrderId,
          vendorId: companyId
        }
      });
      setCompleteOrder(data.splitOrderAtPicking);
    } catch (e) {
      console.log(`ERROR UPDATING ORDER: ${e}`);
    }
  };

  return (
    <Page className="sales-order-picking action-page">
      <div className="action-top-bar">
        <div className="order-details">
          <h1>SO #{vendorSalesOrderNumber}</h1>
          <h2>
            {totalQuantity} Items - {companyName}
          </h2>
        </div>
        <FiXCircle onClick={() => navigate(salesOrdersTabPath('picking'))} />
      </div>
      <div className="action-page-body">
        <div className="print-picking-slip">
          <Button
            type="secondary"
            className="document-print-button"
            fullWidth
            disabled={!pickingSlipDocument?.link || !!sendingKey}
            iconBefore={<FiPrinter />}
            onClick={() => {
              if (!pickingSlipDocument?.link) return;
              const download = () =>
                downloadFile(
                  pickingSlipDocument.link,
                  pickingSlipDocument.humanFilename || 'picking-slip.pdf'
                );
              if (rxPrintEnabled && onSendToPrinter) {
                onSendToPrinter('picking-slip', [{ id: pickingSlipDocument.id, download }]);
              } else download();
            }}
          >
            {!pickingSlipDocument?.link
              ? 'Generating Picking Slip…'
              : sendingKey === 'picking-slip'
              ? 'Sending…'
              : documentActionLabel(rxPrintEnabled, 'Picking Slip')}
          </Button>
        </div>
        {hasChargebackItems && (
          <div
            style={{
              marginBottom: '1.6rem',
              fontSize: '1.4rem',
              lineHeight: 1.3,
              color: 'var(--n100)'
            }}
          >
            <strong style={{ color: 'var(--purple, purple)' }}>CHARGEBACK</strong> lines
            only earn their credit if the units were bought from the manufacturer on the
            agreement, so this app will refuse identical-looking stock from any other
            supplier. Pick from the zones and lots it offers you.
          </div>
        )}
        {pathConfigured ? (
          <div className="walk-bar">
            <FiNavigation className="walk-bar__icon" />
            <div className="walk-bar__text">
              {anchorZone && anchorSource === 'startZone' && pickPath.anchorOnPath && (
                <>
                  Starting at <strong>{anchorZone.name}</strong>, walking{' '}
                  {pickPath.direction === 'backward' ? 'back' : 'forward'}
                </>
              )}
              {anchorZone && anchorSource === 'firstPick' && pickPath.anchorOnPath && (
                <>
                  Routed from your first pick at <strong>{anchorZone.name}</strong>
                </>
              )}
              {anchorZone && !pickPath.anchorOnPath && (
                <>
                  <strong>{anchorZone.name}</strong> is not on the pick path — showing the walk
                  from its start
                </>
              )}
              {!anchorZone && <>In walk order from the start of the path</>}
            </div>
            {anchorSource === 'startZone' ? (
              <button
                type="button"
                className="walk-bar__clear"
                aria-label="Clear starting zone"
                onClick={() => setStartZoneId(null)}
              >
                <FiX />
              </button>
            ) : (
              <ZoneScanButton
                className="walk-bar__start"
                companyId={companyId}
                title="Start from a zone"
                hint="Scan the label of the zone you want to begin at. The rest of the order is routed from there."
                manualEntryLabel="Never mind — start from the beginning"
                onSelect={(zone) => setStartZoneId(zone.id)}
              />
            )}
          </div>
        ) : (
          <div className="walk-bar walk-bar--none">
            No pick path set up for this floor yet, so lines are in order-line order. Walk it
            once from the menu: Pick Path Setup.
          </div>
        )}
        {walkOrdered ? (
          walkOrdered.map(renderLine)
        ) : (
          <>
            {scheduledSalesOrderItems.map(renderLine)}
            <div className="otc-separator">Unserialized Products</div>
            {unserializedSalesOrderItems.map(renderLine)}
          </>
        )}
        <div style={{ marginTop: 'auto' }}>
          {usesTotes && !!someComplete && !tote && (
            <Button
              type="primary"
              fullWidth
              onClick={() =>
                navigate({
                  pathname: '/add-tote',
                  search: `?salesOrderId=${salesOrderId}`
                })
              }
            >
              Add Tote
            </Button>
          )}
          <Button
            type="primary"
            fullWidth
            onClick={
              allComplete ? submitSalesOrderUpdate : () => setPartiallyPickedOpen(true)
            }
            // THE gate. With totes on this is unchanged: nothing leaves picking without a bin.
            // With totes off the order completes on picked quantity alone — which is all the server
            // has ever required (updateSalesOrder does a raw $set and checks no tote, and
            // assertShipDispatchGates has no tote clause).
            disabled={!someComplete || (usesTotes && !tote)}
          >
            Complete
          </Button>
        </div>
      </div>

      <div
        className={['result-panel', (!!completeOrder || !!partiallyPickedOpen) && 'open']
          .filter(Boolean)
          .join(' ')}
      >
        {partiallyPickedOpen && !completeOrder && (
          <>
            <div className="result-message">
              <FiAlertTriangle style={{ color: 'var(--red)' }} />
              <h1>Partially Picked</h1>
              <p>
                Not all the product(s) have been picked for SO #{vendorSalesOrderNumber}.
                To continue, either Split the CO with the partially picked items or go
                back and finish picking.
              </p>
            </div>
            <div className="result-actions">
              <Button type="secondary" fullWidth onClick={submitSplitOrderAtPicking}>
                Split CO and Complete
              </Button>
              <Button
                type="primary"
                fullWidth
                onClick={() => {
                  setPartiallyPickedOpen(false);
                  navigate('/sales-orders', { replace: true });
                }}
              >
                Go Back
              </Button>
            </div>
          </>
        )}
        {!!completeOrder && !partiallyPickedOpen && (
          <>
            <div className="result-message">
              <FiCheckCircle style={{ color: 'var(--green)' }} />
              <h1>Picked Successfully</h1>
              <p>
                {completeOrder?.salesOrderItems?.reduce(
                  (a, { quantity }) => a + quantity,
                  0
                )}{' '}
                product(s) for SO #{completeOrder?.vendorSalesOrderNumber} have
                successfully been picked.
              </p>
            </div>
            <div className="result-actions">
              <Button type="primary" fullWidth onClick={() => navigate('/sales-orders')}>
                Done
              </Button>
            </div>
          </>
        )}
      </div>
    </Page>
  );
}

export default SalesOrderPicking;
